/**
 * Bridges global trade events into ActivityContext as activity feed items.
 *
 * Emits per-trade granularity (no batching) for market trades,
 * order placements, and cancellations.
 */

import { useEffect } from 'react';
import { useActivity } from '@/contexts/ActivityContext';
import { onTradeEvent, type MarketTradeEvent } from '@/lib/tradeEventBus';

export function useTradeActivityBridge(): void {
  const activity = useActivity();

  useEffect(() => {
    const unsub = onTradeEvent((event: MarketTradeEvent) => {
      const shortTicker = event.ticker.replace(/^KX/, '');
      const plural = event.count === 1 ? '' : 's';

      if (event.type === 'order_placed') {
        activity.push({
          type: 'order_placed',
          message: `${shortTicker} ${event.side.toUpperCase()} ${event.count} contract${plural} @ ${event.priceCents}¢`,
          ticker: event.ticker,
          orderId: event.orderId,
          amount: (event.priceCents * event.count) / 100,
        });
      } else if (event.type === 'order_cancelled') {
        activity.push({
          type: 'order_cancelled',
          message: `${shortTicker} ${event.count} contract${plural} cancelled`,
          ticker: event.ticker,
          orderId: event.orderId,
        });
      } else {
        // market_trade — per-trade granularity
        activity.push({
          type: 'market_trade',
          message: `${shortTicker} ${event.side.toUpperCase()} ${event.count} contract${plural} @ ${event.priceCents}¢`,
          ticker: event.ticker,
          amount: (event.priceCents * event.count) / 100,
        });
      }
    });

    return () => {
      unsub();
    };
  }, [activity]);
}
